import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';

export type ApiSubModule = {
  subModuleId: string;
  subModuleName: string;
  subModuleKey: string;
  hasAccess: boolean;
};

export type ApiModule = {
  moduleId: string;
  moduleName: string;
  moduleKey: string;
  hasAccess: boolean;
  subModules: ApiSubModule[];
};

export type SubscriptionInfo = {
  planUniqueId: string | null;
  planName: string | null;
  credits?: number | null;
  subscriptionEnds?: string | null;
};

type ModulesContextType = {
  modules: ApiModule[];
  subscription: SubscriptionInfo | null;
  setWorkspaceModules: (workspaceId: string, modules: ApiModule[], subscription?: SubscriptionInfo | null) => void;
};

const ModulesContext = createContext<ModulesContextType | undefined>(undefined);

export const ModulesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ws = useWorkspace();
  const [modules, setModules] = useState<ApiModule[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);

  // Helper to read persisted values from localStorage for current workspace
  useEffect(() => {
    const workspaceId = ws?.currentWorkspace?.id || (() => {
      try { return sessionStorage.getItem('currentWorkspaceId') || undefined; } catch { return undefined; }
    })();
    if (!workspaceId) return;
    try {
      const rawModules = localStorage.getItem(`modules:${workspaceId}`);
      const rawSub = localStorage.getItem(`subscription:${workspaceId}`);
      setModules(rawModules ? JSON.parse(rawModules) : []);
      setSubscription(rawSub ? JSON.parse(rawSub) : null);
    } catch {
      setModules([]);
      setSubscription(null);
    }
  }, [ws?.currentWorkspace?.id]);

  const setWorkspaceModules = (workspaceId: string, mods: ApiModule[], sub?: SubscriptionInfo | null) => {
    try {
      localStorage.setItem(`modules:${workspaceId}`, JSON.stringify(mods || []));
      if (sub !== undefined) {
        localStorage.setItem(`subscription:${workspaceId}`, JSON.stringify(sub));
      }
    } catch {}
    setModules(mods || []);
    if (sub !== undefined) setSubscription(sub || null);
  };

  const value = useMemo<ModulesContextType>(() => ({
    modules,
    subscription,
    setWorkspaceModules,
  }), [modules, subscription]);

  return (
    <ModulesContext.Provider value={value}>
      {children}
    </ModulesContext.Provider>
  );
};

export const useModules = () => useContext(ModulesContext) as ModulesContextType | undefined;
